import Raven from 'raven-js';
import { useCallback, useRef } from 'react';
import { MAX_DEPTH, getExtractedParameterNames } from './jsonSchemaUtils';
export const useFunctionQueue = appName => {
  const queue = useRef([]);
  const isProcessing = useRef(false);
  const processQueue = useCallback(async usageTracker => {
    if (isProcessing.current || queue.current.length === 0) return;
    isProcessing.current = true;
    const {
      function: fn,
      functionParameters,
      name,
      asyncTimeout = 2000
    } = queue.current[0];
    try {
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error(`Function ${name} execution timed out after ${asyncTimeout}ms`));
        }, asyncTimeout);
        Promise.resolve(fn(functionParameters)).then(response => {
          if (usageTracker) {
            usageTracker.track('interaction', {
              action: 'interactive_mode_auto_execute',
              hostAppName: appName,
              interactiveModeFunctionName: name
            });
          }
          clearTimeout(timeout);
          resolve(String(response));
        }).catch(error => {
          reject(error);
          Raven.captureException(error, {
            extra: {
              message: `Function ${name} execution failed`,
              component: 'useFunctionQueue',
              functionName: name
            }
          });
        });
      });
    } catch (error) {
      Raven.captureException(error, {
        extra: {
          message: `Function ${name} execution timed out after ${asyncTimeout}ms`,
          component: 'useFunctionQueue',
          functionName: name
        }
      });
    }
    queue.current.shift();
    isProcessing.current = false;
    void processQueue();
  }, [appName]);
  const addToQueue = useCallback(({
    function: fn,
    functionParameters,
    name,
    asyncTimeout = 2000,
    usageTracker
  }) => {
    queue.current.push({
      function: fn,
      functionParameters,
      name,
      asyncTimeout
    });
    void processQueue(usageTracker);
  }, [processQueue]);
  return {
    addToQueue
  };
};

// Used with v2.  Everything above should be deleted.
export const formatContextInstructions = actions => {
  const actionsAsMarkDown = actions.map(action => {
    const actionName = `## ${action.name}`;
    const formatValue = (keyPath, value, depth) => {
      if (depth >= MAX_DEPTH) {
        return [`current ${keyPath}: ${JSON.stringify(value)}`];
      }
      if (Array.isArray(value)) {
        // Iterate through array items with index notation
        return value.flatMap((item, index) => formatValue(`${keyPath}[${index}]`, item, depth + 1));
      }
      if (value !== null && typeof value === 'object') {
        // Recursively handle object properties
        return Object.entries(value).flatMap(([childKey, childVal]) => formatValue(`${keyPath}.${childKey}`, childVal, depth + 1));
      }

      // Primitive value or null
      return [`current ${keyPath}: ${JSON.stringify(value)}`];
    };
    const parameterLines = [];
    Object.entries(action.functionParameters).forEach(([paramName, param]) => {
      parameterLines.push(...formatValue(paramName, param.currentValue, 0));
    });
    const parameters = parameterLines.join('\n');
    return parameters ? `${actionName}\n${parameters}` : actionName;
  }).join('\n');
  return `# You may take actions\n${actionsAsMarkDown}`;
};

// // TODO: figure out character limit for external team - and throw error if too long.
export const formatResponseInstructions = additionalLLMInstructions => `# Response Guidelines

## 1. Communication
- Respond in clear, friendly, and concise natural language. Avoid technical jargon unless explicitly asked.
- Use HTML ordered/unordered lists when listing options or suggestions.
- If something is unclear, ask a clarifying follow-up question to better understand the user's request.
- If the requested action cannot be completed, briefly explain why and offer alternative suggestions.

---

## 2. Button metadata (always include)
For every action you return, always include:
- \`buttonText\`: A short label (e.g., "Add Summary", "Update Section", "Add CTA" etc.).
- \`buttonExplanation\`: A sentence describing the action — either what *was done* (if auto-applied) or what *will happen* (if confirmation is required).
- \`buttonHeader\`: A short section title.

---

## 3. Button behavior rules

### Case A — User asks for multiple things in one prompt

Examples:
1) "Summarize this and add a CTA."
2) "Update the executive summary and change the title."
3) "Update the color and shape of the chart."
4) "Add a section for FAQs and move the testimonials above it."
5) "Summarize the data, change the font, and align everything to the center."
6) "Remove the pricing table and replace it with a simpler version."

- **IMPORTANT: Set \`showAsButton: true\` for every action (no exceptions).**

### Case B — User asks for a single thing

Examples:
1) "Update the executive summary."
2) "Add a CTA at the bottom."
3) "Rewrite the introduction to be more concise."
4) "Change the title to something more engaging."
5) "Make the tone more formal."
6) "Add a pricing table."

Choose \`showAsButton\` by intent:
- \`showAsButton: false\` — if the command is clear and direct; apply immediately.
  - In this case, \`buttonExplanation\` should describe what was done.
- \`showAsButton: true\` — if the request is vague, a suggestion, or requires review.
  - In this case, \`buttonExplanation\` should describe what will happen if the user clicks.

---

${additionalLLMInstructions ? `## 4. Page-specific instructions\n${additionalLLMInstructions}` : ''}
`;

// THIS WILL BE REMOVED ONCE https://git.hubteam.com/HubSpot/chatspot-ui/pull/3095 is merged
export const getCurrentPageInteractions = (...___fake) => {
  return {
    pageName: 'TEMPORARY IN ORDER NOT TO BREAK THE BUILD',
    interactions: []
  };
};

// Data for comparing response with state
export const getMatchingDefinedFunction = (customFEActions, functionName) => {
  if (!customFEActions) {
    return null;
  }
  const currentValueOfProperty = customFEActions.find(interactionState => interactionState.name === functionName);
  if (!currentValueOfProperty) {
    Raven.captureMessage('Failed to get matching defined function based on LLM determined function name', {
      extra: {
        component: 'getMatchingDefinedFunction',
        functionName,
        customFEActions
      }
    });
    return null;
  }
  return currentValueOfProperty;
};
export const getCurrentStateOfFunctionValuesByName = (customFEActions, functionName) => {
  const currentValueOfProperty = getMatchingDefinedFunction(customFEActions, functionName);
  if (!currentValueOfProperty) {
    return null;
  }
  const parameterState = Object.entries(currentValueOfProperty.functionParameters).reduce((acc, [key, value]) => {
    acc[key] = value.currentValue;
    return acc;
  }, {});
  return parameterState;
};
const deepCompareValues = (current, llm) => {
  if (current === llm) return true;
  if (current == null || llm == null) return current === llm;

  // Handle arrays
  if (Array.isArray(current) && Array.isArray(llm)) {
    if (current.length !== llm.length) return false;
    return current.every((item, index) => deepCompareValues(item, llm[index]));
  }

  // Handle objects
  if (typeof current === 'object' && typeof llm === 'object') {
    const llmKeys = Object.keys(llm);
    // Only compare the keys that exist in the LLM parameters
    return llmKeys.every(key => deepCompareValues(current[key], llm[key]));
  }

  // Handle primitive values (convert to string for comparison to handle type differences)
  return String(current) === String(llm);
};
export const getAreAllParametersTheSame = (currentValuesOfFunction, llmDeterminedAction) => {
  const llmDefinedParameters = getExtractedParameterNames(llmDeterminedAction);
  return Object.keys(llmDefinedParameters).every(key => {
    const currentValue = currentValuesOfFunction[key];
    const llmValue = llmDefinedParameters[key];
    const isEqual = deepCompareValues(currentValue, llmValue);
    return isEqual;
  });
};
export const getUndoAction = (currentValuesOfFunction, action) => {
  return Object.assign({}, action, currentValuesOfFunction);
};
export const getShouldShowAsButton = (llmDeterminedActions, userDefinedActions) => {
  if (!llmDeterminedActions || !userDefinedActions) {
    return false;
  }
  const matchingFunction = llmDeterminedActions.map(action => {
    return getMatchingDefinedFunction(userDefinedActions, action.functionName);
  });
  if (!matchingFunction) {
    return false;
  }
  const areAnyShowAsButton = llmDeterminedActions === null || llmDeterminedActions === void 0 ? void 0 : llmDeterminedActions.some(action => action.showAsButton);
  const doAnyRequireConfirmation = matchingFunction.some(action => action === null || action === void 0 ? void 0 : action.requiresUserConfirmation);
  return areAnyShowAsButton || doAnyRequireConfirmation;
};